import React, { DetailedHTMLProps, InputHTMLAttributes, useState } from 'react';
import clsx from 'clsx';
import Typography from 'components/Typography';

import { Controller, ControllerProps, useFormContext, Validate } from 'react-hook-form';

import PasswordToggle from './PasswordToggle';

import styles from './FormInput.module.scss';

export interface FormInputProps
  extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'variant' | 'name'> {
  name: ControllerProps['name'];
  validate?: Validate<string> | Record<string, Validate<string>>;
  renderInput?: ControllerProps['render'];
  label?: string;
  variant?: 'primary' | 'secondary';
  color?: 'white' | 'grey';
}

const FormInput: React.FC<FormInputProps> = ({
  name,
  validate,
  renderInput,
  label,
  variant = 'primary',
  color = 'grey',
  ...props
}) => {
  const {
    formState: { errors },
    control,
  } = useFormContext();
  const [type, setType] = useState<React.HTMLInputTypeAttribute>(props.type || 'text');

  const handleTypeChange = (inputType: React.HTMLInputTypeAttribute) => {
    setType(inputType);
  };

  // accessing nested JS objects and arrays by string path
  const error = name.split('.').reduce((p, c) => (p && p[c]) || null, errors);
  const hasError = Boolean(error);

  return (
    <Controller
      name={name}
      control={control}
      rules={{ validate }}
      render={
        renderInput ||
        (p => (
          <div className={clsx(styles.container, styles[variant], styles[color], props.className)}>
            <input
              {...props}
              {...p.field}
              id={p.field.name}
              type={type}
              className={clsx({ [styles.error]: hasError })}
              placeholder=" "
              value={props.value}
            />
            <label htmlFor={p.field.name}>{label}</label>
            {props.type === 'password' && <PasswordToggle onToggle={handleTypeChange} />}
            {hasError && (
              <Typography variant="body4" className={styles.error}>
                {error?.message}
              </Typography>
            )}
          </div>
        ))
      }
    />
  );
};

export default FormInput;
